import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Table, Button, Badge, Card, Row, Col, Modal, Alert } from 'react-bootstrap';
import DemoBanner from '../components/DemoBanner';
import ModalNovoUsuario from '../components/ModalNovoUsuario';
import ModalEditarUsuario from '../components/ModalEditarUsuario';
import ModalDeletarUsuario from '../components/ModalDeletarUsuario';
import ModalImportarCSV from '../components/ModalImportarCSV';
import BrandImage from '../components/BrandImage';
import { DICT } from '../utils/dictionary';
import { API_BASE, getAuthHeaders } from '../utils/api';

const Team = () => {
  const { acesso, token, logout, isDemo } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [show, setShow] = useState({ novo: false, editar: false, deletar: false, importar: false });
  const [edit, setEdit] = useState({});
  const [resetInfo, setResetInfo] = useState(null); // { nome, senha_padrao } ou { nome, erro }
  const [confirmReset, setConfirmReset] = useState(null); // usuário aguardando confirmação

  useEffect(() => {
    if (isDemo) {
      setUsuarios([
        { id: 1, nome: "Maycon Bruno", cargo: "Product Owner", numero_telefone: "11999999999", acesso: "admin", gerente_percentual: 0 },
        { id: 2, nome: "Agnaldo", cargo: "THUMBMAKER", cargo_excecao: "DESENHISTA", condicao_tipo_conteudo: "SHORT", numero_telefone: "11988888888", acesso: "colaborador", gerente_percentual: 0 }, 
        { id: 3, nome: "Lucas Souza", cargo: DICT.CARGOS_ETAPAS[0] || "Especialista", numero_telefone: "11977777777", acesso: "colaborador", gerente_percentual: 0 }
      ]);
      setCargos(["Product Owner", "THUMBMAKER", "DESENHISTA", ...DICT.CARGOS_ETAPAS]);
      return;
    }

    if (acesso === 'admin' || acesso === 'gerente') {
      const fetchData = async () => {
        try {
          const resUsers = await fetch(`${API_BASE}/users/`, { headers: getAuthHeaders(token) });
          if (resUsers.status === 401) return logout();
          const dataUsers = await resUsers.json();
          setUsuarios(Array.isArray(dataUsers) ? dataUsers : []);

          const resCargos = await fetch(`${API_BASE}/users/cargos`, { headers: getAuthHeaders(token) });
          const dataCargos = await resCargos.json();
          setCargos(Array.isArray(dataCargos) ? dataCargos : []);
        } catch (err) {
          console.error("Erro de conexão.", err);
        }
      };
      fetchData();
    }
  }, [acesso, token, isDemo, logout]);

  const usuariosOrdenados = useMemo(() => {
    const ordem = { admin: 0, gerente: 1, colaborador: 2 };
    return [...usuarios].sort((a, b) => {
      const pA = ordem[a.acesso] ?? 99;
      const pB = ordem[b.acesso] ?? 99;
      return pA !== pB ? pA - pB : (a.nome || "").localeCompare(b.nome || "");
    });
  }, [usuarios]);

  const formatPhone = (v) => {
    const n = v?.replace(/\D/g, '');
    return n?.length === 11 ? `(${n.slice(0, 2)}) ${n[2]} ${n.slice(3, 7)}-${n.slice(7)}` : v;
  };

  const formatPercent = (p) => {
    const n = Number(p);
    return isFinite(n) ? `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}%` : '-';
  };

  const handleEdit = (u) => { setEdit(u); setShow(s => ({ ...s, editar: true })); };
  const handleDelete = (u) => { setEdit(u); setShow(s => ({ ...s, deletar: true })); };
  const handleResetSenha = async (u) => {
    if (isDemo) {
      setResetInfo({ nome: u.nome, senha_padrao: 'mudar123' });
      setConfirmReset(null);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/users/${u.id}/reset-senha`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });
      const data = await res.json();
      if (res.ok) setResetInfo({ nome: u.nome, senha_padrao: data.senha_padrao });
      else setResetInfo({ nome: u.nome, erro: data.message || 'Erro ao resetar senha.' });
    } catch { setResetInfo({ nome: u.nome, erro: 'Erro ao conectar com o servidor.' }); }
  };

  if (!['admin', 'gerente', 'analista'].includes(acesso)) return <Navigate to="/videos" />;

  return (
    <div className="main-content container-fluid py-4">
      {isDemo && <DemoBanner />}
      <Row className="align-items-center mb-4">
        <Col xs={12} md={6}>
          <h2 className="mb-0 fw-bold" style={{ color: 'var(--brand-secondary)' }}>
            <i className="bi bi-people-fill me-2" style={{ color: 'var(--brand-primary)' }}></i>Gestão de Equipe
          </h2>
        </Col>
        <Col xs={12} md={6} className="text-md-end d-none d-md-block">
          <BrandImage 
            envKey="REACT_APP_BANNER_TEAM"
            defaultAsset="/client_assets/aero_athos.png" 
            alt="Identidade Visual Equipe"
            style={{ maxWidth: 150, height: 'auto' }} 
          />
        </Col>
      </Row>

      {acesso === 'admin' && (
        <div className="d-flex gap-2 mb-4">
          <Button
            className="shadow-sm border-0 fw-semibold"
            onClick={() => setShow(s => ({ ...s, novo: true }))}
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <i className="bi bi-person-plus-fill me-2"></i>Novo Usuário
          </Button>
          <Button
            variant="outline-secondary"
            className="fw-semibold"
            onClick={() => setShow(s => ({ ...s, importar: true }))}
          >
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>Importar CSV
          </Button>
        </div>
      )}

      <Card className="border-0 shadow-sm overflow-hidden">
        <Table responsive hover className="mb-0">
          <thead style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
            <tr className="text-secondary small text-uppercase fw-bold">
              <th className="px-4 py-3 border-0">Nome</th>
              <th className="py-3 border-0">Cargo</th>
              <th className="py-3 border-0">Telefone</th>
              <th className="py-3 border-0">Acesso</th>
              <th className="py-3 border-0 text-center">{DICT.REWARD} (%)</th>
              <th className="px-4 py-3 border-0 text-end">Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuariosOrdenados.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-5 text-muted">Nenhum membro encontrado.</td></tr>
            ) : (
              usuariosOrdenados.map((u) => (
                <tr key={u.id} className="align-middle border-bottom">
                  <td className="px-4 py-3 fw-bold text-dark">{u.nome}</td>
                  <td className="py-3">
                    <div className="d-flex flex-column">
                      <Badge bg="light" text="dark" className="border fw-normal align-self-start">
                        {u.cargo}
                      </Badge>
                      {u.cargo_excecao && (
                        <small className="mt-1 fw-medium" style={{ color: 'var(--brand-primary)' }} style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                          <i className="bi bi-arrow-return-right me-1"></i>
                          {u.cargo_excecao} EM {u.condicao_tipo_conteudo}S
                        </small>
                      )}
                    </div>
                  </td>
                  <td className="text-muted">{formatPhone(u.numero_telefone) || '-'}</td>
                  <td>
                    <Badge bg={u.acesso === 'admin' ? 'dark' : u.acesso === 'gerente' ? 'primary' : 'secondary'}>
                      {u.acesso.toUpperCase()}
                    </Badge>
                  </td>
                  <td className="text-center fw-medium" style={{ color: 'var(--brand-primary)' }}>
                    {u.acesso === 'gerente' ? formatPercent(u.gerente_percentual) : '-'}
                  </td>
                  <td className="px-4 py-3 text-end">
                    {acesso === 'admin' && (
                      <div className="btn-group gap-2">
                        <Button size="sm" variant="outline-primary" className="border-0" onClick={() => handleEdit(u)} title="Editar">
                          <i className="bi bi-pencil-square"></i>
                        </Button>
                        <Button size="sm" variant="outline-warning" className="border-0" onClick={() => setConfirmReset(u)} title="Resetar senha">
                          <i className="bi bi-key"></i>
                        </Button>
                        <Button size="sm" variant="outline-danger" className="border-0" onClick={() => handleDelete(u)} title="Excluir">
                          <i className="bi bi-trash3"></i>
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      <ModalImportarCSV
        show={show.importar}
        onHide={() => setShow(s => ({ ...s, importar: false }))}
        onSuccess={() => {
          fetch(`${API_BASE}/users/`, { headers: getAuthHeaders(token) })
            .then(r => r.json()).then(d => setUsuarios(Array.isArray(d) ? d : []));
        }}
        token={token}
      />
      <ModalNovoUsuario
        show={show.novo}
        onHide={() => setShow(s => ({ ...s, novo: false }))}
        setUsuarios={setUsuarios}
        usuarios={usuarios}
        cargos={cargos}
        isDemo={isDemo}
      />
      <ModalEditarUsuario
        show={show.editar}
        onHide={() => setShow(s => ({ ...s, editar: false }))}
        edit={edit}
        setUsuarios={setUsuarios}
        usuarios={usuarios}
        cargos={cargos}
        isDemo={isDemo}
      />
      <ModalDeletarUsuario
        show={show.deletar}
        onHide={() => setShow(s => ({ ...s, deletar: false }))}
        edit={edit}
        setUsuarios={setUsuarios}
        usuarios={usuarios}
        isDemo={isDemo}
      />

      <Modal show={!!confirmReset} onHide={() => setConfirmReset(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-key me-2 text-warning"></i>Resetar Senha</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Deseja resetar a senha de <strong>{confirmReset?.nome}</strong>? Ela será redefinida para a senha padrão e o usuário precisará alterá-la no próximo acesso.
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setConfirmReset(null)}>Cancelar</Button>
          <Button variant="warning" onClick={() => { handleResetSenha(confirmReset); setConfirmReset(null); }}>
            <i className="bi bi-key me-2"></i>Resetar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!resetInfo} onHide={() => setResetInfo(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {resetInfo?.erro
              ? <><i className="bi bi-x-circle-fill me-2 text-danger"></i>Erro</>
              : <><i className="bi bi-key-fill me-2 text-warning"></i>Senha Resetada</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resetInfo?.erro
            ? <Alert variant="danger" className="border-0">{resetInfo.erro}</Alert>
            : <>
                <Alert variant="warning" className="border-0">
                  A senha de <strong>{resetInfo?.nome}</strong> foi resetada. Informe a senha padrão abaixo ao usuário.
                </Alert>
                <div className="p-3 rounded-3 text-center" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <div className="small text-muted mb-1">Senha padrão</div>
                  <div className="fw-bold fs-4" style={{ letterSpacing: '2px', fontFamily: 'monospace' }}>{resetInfo?.senha_padrao}</div>
                </div>
              </>}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="primary" className="w-100 border-0 fw-bold" style={{ backgroundColor: 'var(--brand-primary)' }} onClick={() => setResetInfo(null)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Team;